/**
 * Certificate Visual Themes & Template Styling Configuration for School ERP
 * Defines 12+ professional certificate styles, border designs, fonts, and paper sizes.
 */

export const CERTIFICATE_THEMES = [
  {
    id: 'CLASSIC_SCHOOL',
    name: 'Classic School',
    category: 'Institutional',
    primaryColor: '#1e3a8a',
    secondaryColor: '#d97706',
    borderStyle: 'double-ornate',
    fontFamily: "'Cinzel', 'Times New Roman', serif",
    headerLayout: 'centered-crest',
    badgeType: 'ribbon-seal',
    bgTexture: 'clean-cream',
    description: 'Traditional school certificate with deep navy borders, gold trims, and formal serif typography.'
  },
  {
    id: 'ROYAL_GOLD',
    name: 'Royal Gold',
    category: 'Prestige',
    primaryColor: '#854d0e',
    secondaryColor: '#eab308',
    borderStyle: 'gold-filigree',
    fontFamily: "'Playfair Display', Georgia, serif",
    headerLayout: 'heraldic',
    badgeType: 'gold-laurel',
    bgTexture: 'parchment-soft',
    description: 'Regal golden border with intricate antique corner filigrees and prestigious gold seal.'
  },
  {
    id: 'MODERN_BLUE',
    name: 'Modern Professional',
    category: 'Contemporary',
    primaryColor: '#0369a1',
    secondaryColor: '#38bdf8',
    borderStyle: 'geometric-modern',
    fontFamily: "'Outfit', 'Inter', sans-serif",
    headerLayout: 'modern-split',
    badgeType: 'minimal-star',
    bgTexture: 'clean-white',
    description: 'Sleek contemporary design with crisp geometric accent bars and modern sans-serif typography.'
  },
  {
    id: 'ELEGANT_PURPLE',
    name: 'Elegant Purple',
    category: 'Prestige',
    primaryColor: '#581c87',
    secondaryColor: '#c084fc',
    borderStyle: 'ornate-corner',
    fontFamily: "'Cormorant Garamond', 'Times New Roman', serif",
    headerLayout: 'centered-crest',
    badgeType: 'purple-ribbon',
    bgTexture: 'soft-violet',
    description: 'Refined royal amethyst styling with gold and purple accents and formal script titles.'
  },
  {
    id: 'GREEN_ACADEMIC',
    name: 'Green Academic',
    category: 'Institutional',
    primaryColor: '#065f46',
    secondaryColor: '#34d399',
    borderStyle: 'classic-ivy',
    fontFamily: "'Merriweather', Georgia, serif",
    headerLayout: 'centered-crest',
    badgeType: 'ivy-wreath',
    bgTexture: 'clean-mint',
    description: 'Noble emerald green motif featuring traditional ivy-leaf wreath accents.'
  },
  {
    id: 'MINIMAL_OFFICIAL',
    name: 'Minimal Official',
    category: 'Formal',
    primaryColor: '#18181b',
    secondaryColor: '#71717a',
    borderStyle: 'clean-thin',
    fontFamily: "'Inter', sans-serif",
    headerLayout: 'official-compact',
    badgeType: 'none',
    bgTexture: 'crisp-white',
    description: 'Strictly formal, ultra-clean institutional layout with high print contrast and minimal decoration.'
  },
  {
    id: 'TRADITIONAL_SCHOOL',
    name: 'Traditional Indian School',
    category: 'Traditional',
    primaryColor: '#9a3412',
    secondaryColor: '#f59e0b',
    borderStyle: 'indian-mandala',
    fontFamily: "'Rozha One', 'Georgia', serif",
    headerLayout: 'traditional-emblem',
    badgeType: 'mandala-seal',
    bgTexture: 'parchment-antique',
    description: 'Traditional design with ornamental corner crests and warm saffron/rust hues.'
  },
  {
    id: 'PREMIUM_BORDER',
    name: 'Premium Guilloche Border',
    category: 'Security',
    primaryColor: '#0f172a',
    secondaryColor: '#f59e0b',
    borderStyle: 'guilloche-security',
    fontFamily: "'Cinzel', serif",
    headerLayout: 'centered-crest',
    badgeType: 'embossed-gold',
    bgTexture: 'guilloche-fine',
    description: 'High-security certificate styling with fine guilloche lace patterns and embossed gold seal.'
  },
  {
    id: 'ACHIEVEMENT_GOLD',
    name: 'Achievement Gold',
    category: 'Awards',
    primaryColor: '#b45309',
    secondaryColor: '#fbbf24',
    borderStyle: 'star-burst',
    fontFamily: "'Montserrat', sans-serif",
    headerLayout: 'award-hero',
    badgeType: 'gold-medal',
    bgTexture: 'radial-gold',
    description: 'Celebratory award format with prominent gold medallion and celebratory laurel ribbons.'
  },
  {
    id: 'SPORTS_CERTIFICATE',
    name: 'Sports Championship',
    category: 'Sports',
    primaryColor: '#1e40af',
    secondaryColor: '#ef4444',
    borderStyle: 'sport-dynamic',
    fontFamily: "'Rajdhani', 'Oswald', sans-serif",
    headerLayout: 'sport-banner',
    badgeType: 'trophy-badge',
    bgTexture: 'dynamic-mesh',
    description: 'Energetic athletics theme with dual color ribbons, sports wreath, and bold typography.'
  },
  {
    id: 'CULTURAL_CERTIFICATE',
    name: 'Cultural & Arts Fest',
    category: 'Arts',
    primaryColor: '#831843',
    secondaryColor: '#f472b6',
    borderStyle: 'artistic-wave',
    fontFamily: "'Playfair Display', serif",
    headerLayout: 'artistic-flourish',
    badgeType: 'cultural-badge',
    bgTexture: 'soft-rose',
    description: 'Artistic certificate for literary, musical, and dramatic achievements with flourish accents.'
  },
  {
    id: 'GOVERNMENT_STYLE',
    name: 'Government & Board Format',
    category: 'Government',
    primaryColor: '#1e293b',
    secondaryColor: '#0284c7',
    borderStyle: 'govt-standard',
    fontFamily: "'Times New Roman', serif",
    headerLayout: 'bilingual-govt',
    badgeType: 'official-crest',
    bgTexture: 'watermark-shield',
    description: 'Official state department/board style with structured bilingual formatting and official seal placement.'
  },
  {
    id: 'FORMAL_LETTER',
    name: 'Formal Institutional Letter',
    category: 'Letters',
    primaryColor: '#0f172a',
    secondaryColor: '#64748b',
    borderStyle: 'letterhead-minimal',
    fontFamily: "'Times New Roman', Georgia, serif",
    headerLayout: 'letterhead-top',
    badgeType: 'none',
    bgTexture: 'clean-white',
    description: 'Standard institutional letterhead format featuring Ref No., Date, salutation, justified text, and official sign-off.'
  },
  {
    id: 'LANDSCAPE_AWARD',
    name: 'Landscape Presentation Certificate',
    category: 'Awards',
    primaryColor: '#1e3a8a',
    secondaryColor: '#f59e0b',
    borderStyle: 'landscape-ornate',
    fontFamily: "'Cinzel', 'Playfair Display', serif",
    headerLayout: 'landscape-balanced',
    badgeType: 'gold-medal',
    bgTexture: 'clean-cream',
    description: 'Wide A4 landscape layout (297mm x 210mm) ideal for awards, honors, and graduation ceremonies.'
  },
  {
    id: 'DEDICATED_TC',
    name: 'Dedicated Transfer Certificate (TC)',
    category: 'Statutory',
    primaryColor: '#0f172a',
    secondaryColor: '#1e3a8a',
    borderStyle: 'tabular-official',
    fontFamily: "'Times New Roman', 'Arial', sans-serif",
    headerLayout: 'tc-header',
    badgeType: 'none',
    bgTexture: 'clean-white',
    description: 'Official Ministry/State Board compliant Transfer Certificate with 20+ statutory tabular fields.'
  },
  {
    id: 'COMPREHENSIVE_CLEARANCE',
    name: 'All-Subject Clearance & No Dues Sheet',
    category: 'Administrative',
    primaryColor: '#1e3a8a',
    secondaryColor: '#059669',
    borderStyle: 'tabular-official',
    fontFamily: "'Times New Roman', 'Arial', sans-serif",
    headerLayout: 'tc-header',
    badgeType: 'none',
    bgTexture: 'clean-white',
    description: 'All-in-one comprehensive school clearance slip with all-subject verification grid, departmental clearances, and parent signature.'
  }
];

export const PAGE_SIZES = [
  { id: 'A4', label: 'A4 (210 × 297 mm)', widthMm: 210, heightMm: 297 },
  { id: 'A5', label: 'A5 (148 × 210 mm)', widthMm: 148, heightMm: 210 },
  { id: 'LETTER', label: 'US Letter (8.5 × 11 in)', widthMm: 215.9, heightMm: 279.4 },
  { id: 'LEGAL', label: 'US Legal (8.5 × 14 in)', widthMm: 215.9, heightMm: 355.6 }
];

export const ORIENTATIONS = [
  { id: 'PORTRAIT', label: 'Portrait (Vertical)' },
  { id: 'LANDSCAPE', label: 'Landscape (Horizontal)' }
];

export const SIGNATURE_LAYOUTS = [
  { id: 'SINGLE', label: '1 Signature (Principal / Headmaster)' },
  { id: 'DUAL', label: '2 Signatures (Class Teacher + Principal)' },
  { id: 'TRIPLE', label: '3 Signatures (Class Teacher + Headmaster + Principal)' },
  { id: 'TC_OFFICIAL', label: 'TC Official (Class Teacher + Verified Clerk + Principal)' }
];

export const BORDER_STYLES = [
  { id: 'double-ornate', label: 'Classic Double Ornate Border' },
  { id: 'gold-filigree', label: 'Royal Gold Filigree Corner Border' },
  { id: 'geometric-modern', label: 'Modern Geometric Border' },
  { id: 'guilloche-security', label: 'Security Guilloche Lace Border' },
  { id: 'clean-thin', label: 'Clean Minimal Line Border' },
  { id: 'classic-ivy', label: 'Academic Ivy Border' },
  { id: 'tabular-official', label: 'Official Tabular Border' }
];

export const FONT_OPTIONS = [
  { id: 'Cinzel', label: 'Cinzel (Classical Academic Serif)', css: "'Cinzel', serif" },
  { id: 'Playfair Display', label: 'Playfair Display (Elegant Editorial Serif)', css: "'Playfair Display', serif" },
  { id: 'Times New Roman', label: 'Times New Roman (Standard Official Serif)', css: "'Times New Roman', serif" },
  { id: 'Outfit', label: 'Outfit (Modern Professional Sans)', css: "'Outfit', sans-serif" },
  { id: 'Inter', label: 'Inter (Clean High-Legibility Sans)', css: "'Inter', sans-serif" },
  { id: 'Merriweather', label: 'Merriweather (Warm Academic Serif)', css: "'Merriweather', serif" }
];
